// One timeline, everywhere. An entry attaches to any record, carries a kind
// (note / call / meeting / update / decision) and a source (human / agent), and
// threads comments underneath. v1's notes fold in as entries of kind `note`, and
// Atlas's per-project update log is the same component with a different label.
//
// The composer is the whole point of the merge: log one call that @mentions the
// org, the demo, and the deal, and all four records show it.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { formatShortDate, relativeShort, relativeTime, todayIso } from '../lib/format'
import { newId } from '../lib/ids'
import { ENTRY_KINDS, entryKind, mentionClass, titleField } from '../lib/model'
import { commentsFor, entriesFor, entriesMentioning, type EntryRecord } from '../lib/reducer'
import { useStore } from '../lib/store'
import { AgentAvatar, ArchivedToggle, Avatar, Button, ConfirmAction, EditableText } from './common'
import { Icon, iconName } from './Icon'
import { Markdown } from './Markdown'
import { MentionInput } from './MentionInput'
import { DateCell } from './Picker'

/**
 * The one composer. Mentions, because every place a person writes prose in crum is a
 * place they may need to name a record — and a mention is the only thing in the app
 * that makes a note findable from the record it is about. Exported so the row card's
 * conversation uses this rather than a second, plainer box.
 */
export function Composer({
  placeholder,
  submitLabel,
  submitOnEnter = false,
  minHeight = 40,
  withKind = false,
  onSave,
}: {
  placeholder: string
  submitLabel: string
  submitOnEnter?: boolean
  minHeight?: number
  withKind?: boolean
  onSave: (body: string, kind: string, occurredAt: string) => void
}) {
  const [body, setBody] = useState('')
  const [kind, setKind] = useState('note')
  const [when, setWhen] = useState(todayIso())
  const [k, setK] = useState(0)

  const save = () => {
    if (!body.trim()) return
    onSave(body, kind, when)
    setBody('')
    setWhen(todayIso())
    setK((v) => v + 1)
  }

  return (
    <div className="space-y-2">
      <MentionInput
        key={k}
        value=""
        onChange={setBody}
        placeholder={placeholder}
        submitOnEnter={submitOnEnter}
        onSubmit={save}
        minHeight={minHeight}
      />
      <div className="flex flex-wrap items-center gap-2">
        {withKind && (
          <>
            <span className="flex flex-wrap items-center gap-1">
              {ENTRY_KINDS.map((ek) => (
                <button
                  key={ek.key}
                  type="button"
                  onClick={() => setKind(ek.key)}
                  className="rounded-control inline-flex items-center gap-1 px-2 py-1"
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 'var(--text-micro)',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    background: kind === ek.key ? 'var(--work-soft)' : 'transparent',
                    color: kind === ek.key ? 'var(--marine)' : 'var(--muted)',
                  }}
                >
                  <Icon name={iconName(ek.icon)} size={12} />
                  {ek.label}
                </button>
              ))}
            </span>
            {/* occurredAt is separate from createdAt, so logging yesterday's call
                today puts it where it happened. */}
            <span className="meta inline-flex items-center gap-1">
              <Icon name="CalendarDays" size={12} />
              <DateCell
                label="When this happened"
                value={when}
                onChange={(v) => setWhen(v || todayIso())}
              />
            </span>
          </>
        )}
        <span className="ml-auto">
          <Button variant="work" onClick={save}>
            <Icon name="CornerDownLeft" size={14} /> {submitLabel}
          </Button>
        </span>
      </div>
    </div>
  )
}

function Entry({ entry, elsewhere = false }: { entry: EntryRecord; elsewhere?: boolean }) {
  const { state, dispatch } = useStore()
  const navigate = useNavigate()
  // A note is the most-edited thing in the app: you write it fast during a call and fix
  // it after. Editing rewrites nothing in the log — it appends an `entry.update`.
  const [editing, setEditing] = useState(false)
  const [showGone, setShowGone] = useState(false)
  const [draft, setDraft] = useState(entry.body)
  const saveEdit = () => {
    // An unchanged body would append an event that says nothing happened.
    if (draft !== entry.body)
      void dispatch({ type: 'entry.update', subject: entry.id, data: { body: draft } })
    setEditing(false)
  }
  const allComments = commentsFor(state, entry.id, true)
  const comments = allComments.filter((c) => !c.archived)
  const goneComments = allComments.filter((c) => c.archived)
  const ek = entryKind(entry.kind)
  const subject = state.entities[entry.subject]
  const who = entry.source === 'agent' ? 'An agent' : (entry.createdBy?.name ?? 'unknown')

  return (
    <div
      className="grid gap-3 py-3.5"
      style={{ gridTemplateColumns: 'minmax(0,1fr)', opacity: entry.archived ? 0.6 : 1 }}
    >
      <div className="flex items-start gap-2.5">
        {entry.source === 'agent' ? (
          <AgentAvatar size={20} />
        ) : (
          <Avatar actor={entry.createdBy} size={20} />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-body flex flex-wrap items-baseline gap-x-2 gap-y-1 leading-snug">
            <span className="font-medium">{who}</span>
            <span style={{ color: 'var(--muted)' }}>{ek.verb}</span>
            {elsewhere && subject && (
              <span style={{ color: 'var(--muted)' }}>
                on{' '}
                <button
                  type="button"
                  onClick={() => navigate(`/e/${subject.id}`)}
                  className={mentionClass(subject.kind)}
                >
                  {String(subject.fields[titleField(subject.kind)] ?? 'Untitled')}
                </button>
              </span>
            )}
            <span className="meta">
              <EditableText
                value={entry.occurredAt.slice(0, 10)}
                type="date"
                onSave={(v) =>
                  void dispatch({
                    type: 'entry.update',
                    subject: entry.id,
                    data: { occurredAt: v },
                  })
                }
                style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-label)' }}
              />
            </span>
            {entry.archived && <span className="badge-archived">Archived</span>}
            <span className="ml-auto flex items-center gap-2">
              {!entry.archived && !editing && (
                <button
                  type="button"
                  onClick={() => {
                    setDraft(entry.body)
                    setEditing(true)
                  }}
                  className="opacity-40 hover:opacity-100"
                  title="Edit"
                  aria-label="Edit this entry"
                >
                  <Icon name="Pencil" size={12} />
                </button>
              )}
              {entry.archived ? (
                <button
                  type="button"
                  onClick={() => void dispatch({ type: 'entry.restore', subject: entry.id })}
                  className="text-label font-medium"
                  style={{ color: 'var(--accent)' }}
                >
                  Restore
                </button>
              ) : (
                <ConfirmAction
                  title="Archive this entry"
                  confirmLabel="Archive"
                  onConfirm={() => void dispatch({ type: 'entry.archive', subject: entry.id })}
                />
              )}
            </span>
          </div>

          {editing ? (
            <div className="mt-1.5">
              <MentionInput
                value={draft}
                onChange={setDraft}
                placeholder="What happened"
                minHeight={72}
                onSubmit={() => saveEdit()}
              />
              <div className="mt-1.5 flex items-center gap-2">
                <Button variant="primary" size="dense" onClick={saveEdit}>
                  Save
                </Button>
                <Button variant="quiet" size="dense" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Markdown body={entry.body} className="prose-body mt-1.5" />
          )}

          {/* A reply could be written and never taken back: every other record in
              the log could be archived and a comment could not, so a reply sent to
              the wrong thread stayed there for good. */}
          {(comments.length > 0 || goneComments.length > 0) && (
            <div
              className="mt-3 space-y-2.5 border-l pl-3"
              style={{ borderColor: 'var(--rule-soft)' }}
            >
              {[...comments, ...(showGone ? goneComments : [])].map((c) => (
                <div
                  key={c.id}
                  className="group flex gap-2"
                  style={{ opacity: c.archived ? 0.6 : 1 }}
                >
                  <Avatar actor={c.createdBy} size={20} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-body font-medium">
                        {c.createdBy?.name ?? 'unknown'}
                      </span>
                      <span className="meta">{relativeTime(c.createdAt)}</span>
                      <span className="ml-auto">
                        {c.archived ? (
                          <button
                            type="button"
                            onClick={() =>
                              void dispatch({ type: 'comment.restore', subject: c.id })
                            }
                            className="text-label font-medium"
                            style={{ color: 'var(--accent)' }}
                          >
                            Restore
                          </button>
                        ) : (
                          <span className="opacity-0 transition-opacity group-hover:opacity-100">
                            <ConfirmAction
                              icon="X"
                              title="Archive this reply"
                              confirmLabel="Archive"
                              onConfirm={() =>
                                void dispatch({ type: 'comment.archive', subject: c.id })
                              }
                            />
                          </span>
                        )}
                      </span>
                    </div>
                    <Markdown body={c.body} className="text-body" />
                  </div>
                </div>
              ))}
              {goneComments.length > 0 && (
                <ArchivedToggle
                  count={goneComments.length}
                  on={showGone}
                  onToggle={() => setShowGone((v) => !v)}
                />
              )}
            </div>
          )}

          {!entry.archived && <Reply entryId={entry.id} />}
        </div>
      </div>
    </div>
  )
}

function Reply({ entryId }: { entryId: string }) {
  const { dispatch } = useStore()
  const [open, setOpen] = useState(false)
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1.5"
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 'var(--text-micro)',
          letterSpacing: '0.05em',
          color: 'var(--faint)',
        }}
      >
        <Icon name="MessageSquare" size={12} /> REPLY
      </button>
    )
  }
  return (
    <div className="mt-2.5">
      <Composer
        placeholder="Reply…"
        submitLabel="Reply"
        submitOnEnter
        onSave={(body) => {
          void dispatch({
            type: 'comment.create',
            subject: newId('cmt'),
            data: { entry: entryId, body },
          })
          setOpen(false)
        }}
      />
    </div>
  )
}

/**
 * The merged timeline for a record: entries filed here, plus every entry filed
 * elsewhere that @mentions it. One call logged against a demo shows up on the
 * org it mentions, with no one filing it twice.
 */
export function EntryThread({
  subjectId,
  placeholder = 'Log a note, meeting, or decision. Type @ to link a record.',
  includeMentions = true,
  limit,
  onMore,
}: {
  subjectId: string
  placeholder?: string
  includeMentions?: boolean
  /** show only the newest N, for a page that keeps the composer but not the whole
   *  history — the client page's Overview, which has a Timeline tab beside it */
  limit?: number | undefined
  /** where the rest lives, rendered as one line under a limited thread */
  onMore?: (() => void) | undefined
}) {
  const { state, dispatch } = useStore()
  const [showArchived, setShowArchived] = useState(false)

  const own = entriesFor(state, subjectId, showArchived)
  const mentioned = includeMentions ? entriesMentioning(state, subjectId) : []
  const archivedCount =
    entriesFor(state, subjectId, true).length - entriesFor(state, subjectId).length

  const merged = [
    ...own.map((e) => ({ e, elsewhere: false })),
    ...mentioned.map((e) => ({ e, elsewhere: true })),
  ].sort((a, b) => (a.e.occurredAt < b.e.occurredAt ? 1 : a.e.occurredAt > b.e.occurredAt ? -1 : 0))

  return (
    <div>
      <Composer
        placeholder={placeholder}
        submitLabel="Log it"
        minHeight={62}
        withKind
        onSave={(body, kind, occurredAt) =>
          void dispatch({
            type: 'entry.create',
            subject: newId('ent'),
            data: { subject: subjectId, body, kind, source: 'human', occurredAt },
          })
        }
      />

      {/* The same control every list in the app uses, rather than the fourth copy of
          it that stood here. */}
      <div className="mt-3 -ml-2">
        <ArchivedToggle
          count={archivedCount}
          on={showArchived}
          onToggle={() => setShowArchived((v) => !v)}
        />
      </div>

      {merged.length === 0 ? (
        <p className="text-body py-6 text-center" style={{ color: 'var(--faint)' }}>
          No entries yet.
        </p>
      ) : (
        <div className="rows mt-2">
          {(limit ? merged.slice(0, limit) : merged).map(({ e, elsewhere }) => (
            <Entry key={e.id} entry={e} elsewhere={elsewhere} />
          ))}
        </div>
      )}

      {limit !== undefined && merged.length > limit && (
        <button type="button" onClick={onMore} className="meta mt-2 underline">
          {merged.length - limit} more
        </button>
      )}
    </div>
  )
}

/** A read-only timeline row, for pages that render entries outside a thread. */
export function EntryRow({ entry }: { entry: EntryRecord }) {
  const { state } = useStore()
  const navigate = useNavigate()
  const subject = state.entities[entry.subject]
  const ek = entryKind(entry.kind)
  return (
    <div className="grid gap-3 py-3" style={{ gridTemplateColumns: '76px minmax(0,1fr)' }}>
      <div className="meta pt-0.5">
        {formatShortDate(entry.occurredAt)}
        <div style={{ color: 'var(--faint)' }}>{relativeShort(entry.occurredAt)}</div>
      </div>
      <div className="min-w-0">
        <div className="text-body flex flex-wrap items-center gap-x-2 gap-y-1">
          {entry.source === 'agent' ? (
            <AgentAvatar size={20} />
          ) : (
            <Avatar actor={entry.createdBy} size={20} />
          )}
          <span className="font-medium">
            {entry.source === 'agent' ? 'An agent' : (entry.createdBy?.name ?? 'unknown')}
          </span>
          <span style={{ color: 'var(--muted)' }}>{ek.verb}</span>
          {subject && (
            <span style={{ color: 'var(--muted)' }}>
              on{' '}
              <button
                type="button"
                onClick={() => navigate(`/e/${subject.id}`)}
                className={mentionClass(subject.kind)}
              >
                {String(subject.fields[titleField(subject.kind)] ?? 'Untitled')}
              </button>
            </span>
          )}
        </div>
        <Markdown body={entry.body} className="prose-body mt-1" />
      </div>
    </div>
  )
}
